import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import styled from '@emotion/styled';
import useResizeCallback, { ResizeCallbackTypes, } from '@polestar/component-warehouse-react/hooks/useResizeCallback';
import useWindowSize from '@polestar/component-warehouse-react/hooks/useWindowSize';
import { hasPassiveSupport, isBrowser } from '@polestar/component-warehouse-react/helpers/supports';
import { clamp } from '@polestar/component-warehouse-react/helpers/mathematical';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
/**
 * @internal
 */
export const ScrollContext = createContext({});
export const useScrollContext = () => useContext(ScrollContext);
const ScrollObserverContext = createContext({
    getProgress: () => 0,
    addProgressListener: () => null,
    removeProgressListener: () => null,
});
export const useScrollObserver = () => useContext(ScrollObserverContext);
// TODO: test accessibility, aria-* needed?
const ScrollObserver = forwardRef(({ children, onProgress, ...props }, ref) => {
    const [dimensions, setDimensions] = useState({
        height: 0,
        top: 0,
    });
    const progressRef = useRef(0);
    const [scrollListeners, setScrollListeners] = useState([]);
    const [showScrollProgressBar, setShowScrollProgressBar] = useState(false);
    const { scrollElement } = useScrollContext();
    const { innerHeight } = useWindowSize();
    const containerRef = useResizeCallback((node) => {
        if (innerHeight !== undefined) {
            // determine the height which is used to calculate the progress
            const currentScrollPosition = (scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollTop) || window.pageYOffset;
            const boundingBox = node.getBoundingClientRect();
            setDimensions({
                top: Math.round(boundingBox.top + currentScrollPosition),
                height: Math.round(boundingBox.height),
            });
        }
    }, [innerHeight, scrollElement], ResizeCallbackTypes.height);
    const combinedRef = useCombinedRef(ref, containerRef);
    useEffect(() => {
        /**
         * when inside a component (Dialog to start with) the user can scroll from start to finish
         * without any top/bottom offset. (no known use-cases yet to do otherwise)
         * when used inside the page, no context is provided and dimensions are used
         *
         * the problem is that when inside a Dialog you cannot use getBoundingClientRect to
         * determine the top/bottom offset to determine when to show the progress bar.
         *
         * TODO: assumption still valid that isInsideComponent never has top/bottom offset?
         */
        const handleScroll = () => {
            const isInsideComponent = !!scrollElement;
            if (isBrowser) {
                const currentScrollPosition = (scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollTop) || window.pageYOffset;
                const startPosition = dimensions.top < 0 ? 0 : dimensions.top || 0;
                const topOffset = (isInsideComponent ? startPosition - ((scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.offsetTop) || 0) : startPosition) ||
                    0;
                const availableHeight = (isInsideComponent
                    ? scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.clientHeight
                    : document.documentElement.clientHeight) || 0;
                const currentProgress = clamp(((currentScrollPosition + availableHeight - topOffset) / dimensions.height) * 100, 0, 100);
                const hideBar = currentProgress >= 100 || currentScrollPosition + availableHeight < startPosition;
                progressRef.current = currentProgress;
                setShowScrollProgressBar(!hideBar);
                onProgress === null || onProgress === void 0 ? void 0 : onProgress(currentProgress);
                scrollListeners.forEach((listener) => listener(currentProgress));
            }
        };
        handleScroll();
        (scrollElement || window).addEventListener('scroll', handleScroll, hasPassiveSupport ? { passive: true } : false);
        return () => (scrollElement || window).removeEventListener('scroll', handleScroll);
    }, [dimensions, onProgress, scrollElement, containerRef, scrollListeners]);
    const handleProgress = useCallback(() => {
        return progressRef.current;
    }, [progressRef]);
    const addProgressListener = useCallback((listener) => {
        setScrollListeners((prevListeners) => [...prevListeners, listener]);
    }, []);
    const removeProgressListener = useCallback((listener) => {
        setScrollListeners((prevListeners) => prevListeners.filter((l) => l !== listener));
    }, []);
    return (React.createElement(Container, { ...props, ref: combinedRef },
        React.createElement(ScrollObserverContext.Provider, { value: {
                getProgress: handleProgress,
                showScrollProgressBar,
                addProgressListener,
                removeProgressListener,
            } }, children)));
});
ScrollObserver.displayName = 'ScrollObserver';
export default ScrollObserver;
const Container = styled.div ``;
Container.displayName = `Container`;
